import { useEffect, useRef, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";

// ── Constants ────────────────────────────────────────────────────────────────

const MINUTELY_INTERVALS = [1, 5, 10, 15, 20, 30];
const HOURLY_INTERVALS = [1, 2, 3, 4, 6, 8, 12];
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5); // 0,5,10,...,55
const DAYS_OF_MONTH = Array.from({ length: 28 }, (_, i) => i + 1);

const DAYS_OF_WEEK = [
  { value: 1, label: "Montag" },
  { value: 2, label: "Dienstag" },
  { value: 3, label: "Mittwoch" },
  { value: 4, label: "Donnerstag" },
  { value: 5, label: "Freitag" },
  { value: 6, label: "Samstag" },
  { value: 0, label: "Sonntag" },
];

const FREQUENCY_OPTIONS = [
  { value: "manual", label: "Manuell" },
  { value: "minutely", label: "Minütlich" },
  { value: "hourly", label: "Stündlich" },
  { value: "daily", label: "Täglich" },
  { value: "weekly", label: "Wöchentlich" },
  { value: "monthly", label: "Monatlich" },
  { value: "custom", label: "Benutzerdefiniert" },
];

const DEFAULT_STATE = {
  frequency: "manual",
  min_interval: 15, // every N minutes
  hr_interval: 6, // every N hours
  minute: 0,
  hour: 6,
  day_of_week: 1,
  day_of_month: 1,
  custom_cron: "",
};

// ── Parse / build ─────────────────────────────────────────────────────────────

function parseCron(cron) {
  if (!cron?.trim()) return { frequency: "manual" };

  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return { frequency: "custom", custom_cron: cron };

  const [min, hour, dom, month, dow] = parts;
  const isNum = (s) => /^\d+$/.test(s);

  // Every N minutes: */N * * * *
  if (
    min.startsWith("*/") &&
    hour === "*" &&
    dom === "*" &&
    month === "*" &&
    dow === "*"
  ) {
    return {
      frequency: "minutely",
      min_interval: parseInt(min.slice(2)) || 15,
    };
  }

  // Every N hours: M */N * * *
  if (
    isNum(min) &&
    hour.startsWith("*/") &&
    dom === "*" &&
    month === "*" &&
    dow === "*"
  ) {
    return {
      frequency: "hourly",
      hr_interval: parseInt(hour.slice(2)) || 6,
      minute: parseInt(min),
    };
  }

  // Daily: M H * * *
  if (
    isNum(min) &&
    isNum(hour) &&
    dom === "*" &&
    month === "*" &&
    dow === "*"
  ) {
    return { frequency: "daily", hour: parseInt(hour), minute: parseInt(min) };
  }

  // Weekly: M H * * DOW
  if (isNum(min) && isNum(hour) && dom === "*" && month === "*" && isNum(dow)) {
    return {
      frequency: "weekly",
      hour: parseInt(hour),
      minute: parseInt(min),
      day_of_week: parseInt(dow),
    };
  }

  // Monthly: M H D * *
  if (isNum(min) && isNum(hour) && isNum(dom) && month === "*" && dow === "*") {
    return {
      frequency: "monthly",
      hour: parseInt(hour),
      minute: parseInt(min),
      day_of_month: parseInt(dom),
    };
  }

  return { frequency: "custom", custom_cron: cron };
}

function buildCron(state) {
  const {
    frequency,
    min_interval,
    hr_interval,
    minute,
    hour,
    day_of_week,
    day_of_month,
    custom_cron,
  } = state;
  switch (frequency) {
    case "minutely":
      return `*/${min_interval} * * * *`;
    case "hourly":
      return `${minute} */${hr_interval} * * *`;
    case "daily":
      return `${minute} ${hour} * * *`;
    case "weekly":
      return `${minute} ${hour} * * ${day_of_week}`;
    case "monthly":
      return `${minute} ${hour} ${day_of_month} * *`;
    case "custom":
      return custom_cron ?? "";
    default:
      return ""; // manual
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const pad2 = (n) => String(n).padStart(2, "0");

function SmallSelect({ label, value, onChange, children, width = 90 }) {
  return (
    <FormControl size="small" sx={{ minWidth: width }}>
      {label && <InputLabel>{label}</InputLabel>}
      <Select
        value={value}
        label={label ?? undefined}
        onChange={(e) => onChange(e.target.value)}
      >
        {children}
      </Select>
    </FormControl>
  );
}

function Word({ children }) {
  return (
    <Typography
      variant="body2"
      sx={{ alignSelf: "center", whiteSpace: "nowrap" }}
    >
      {children}
    </Typography>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function CronEditor({ value, onChange }) {
  const [state, setState] = useState(() => ({
    ...DEFAULT_STATE,
    ...parseCron(value ?? ""),
  }));
  const prevValue = useRef(value);

  // Sync when value changes from outside (e.g. modal loads saved record)
  useEffect(() => {
    if (value !== prevValue.current) {
      prevValue.current = value;
      setState((s) => ({ ...DEFAULT_STATE, ...s, ...parseCron(value ?? "") }));
    }
  }, [value]);

  function update(patch) {
    setState((prev) => {
      const next = { ...prev, ...patch };

      // Clamp interval to valid options when switching frequency
      if (
        patch.frequency === "minutely" &&
        !MINUTELY_INTERVALS.includes(next.min_interval)
      )
        next.min_interval = 15;
      if (
        patch.frequency === "hourly" &&
        !HOURLY_INTERVALS.includes(next.hr_interval)
      )
        next.hr_interval = 6;

      onChange(buildCron(next));
      return next;
    });
  }

  const {
    frequency,
    min_interval,
    hr_interval,
    minute,
    hour,
    day_of_week,
    day_of_month,
    custom_cron,
  } = state;
  const cron_preview = buildCron(state);
  const showTime = ["daily", "weekly", "monthly"].includes(frequency);

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
        {/* Frequency */}
        <SmallSelect
          label="Häufigkeit"
          value={frequency}
          onChange={(v) => update({ frequency: v })}
          width={180}
        >
          {FREQUENCY_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </SmallSelect>

        {/* Minutely: every N minutes */}
        {frequency === "minutely" && (
          <>
            <Word>alle</Word>
            <SmallSelect
              value={min_interval}
              onChange={(v) => update({ min_interval: Number(v) })}
            >
              {MINUTELY_INTERVALS.map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </SmallSelect>
            <Word>Minuten</Word>
          </>
        )}

        {/* Hourly: every N hours at :MM */}
        {frequency === "hourly" && (
          <>
            <Word>alle</Word>
            <SmallSelect
              value={hr_interval}
              onChange={(v) => update({ hr_interval: Number(v) })}
            >
              {HOURLY_INTERVALS.map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </SmallSelect>
            <Word>Stunden zur Minute</Word>
            <SmallSelect
              value={minute}
              onChange={(v) => update({ minute: Number(v) })}
            >
              {MINUTES.map((m) => (
                <MenuItem key={m} value={m}>
                  :{pad2(m)}
                </MenuItem>
              ))}
            </SmallSelect>
          </>
        )}

        {/* Weekly: on day */}
        {frequency === "weekly" && (
          <>
            <Word>am</Word>
            <SmallSelect
              value={day_of_week}
              onChange={(v) => update({ day_of_week: Number(v) })}
              width={140}
            >
              {DAYS_OF_WEEK.map((d) => (
                <MenuItem key={d.value} value={d.value}>
                  {d.label}
                </MenuItem>
              ))}
            </SmallSelect>
          </>
        )}

        {/* Monthly: on Nth */}
        {frequency === "monthly" && (
          <>
            <Word>am</Word>
            <SmallSelect
              value={day_of_month}
              onChange={(v) => update({ day_of_month: Number(v) })}
            >
              {DAYS_OF_MONTH.map((d) => (
                <MenuItem key={d} value={d}>
                  {d}.
                </MenuItem>
              ))}
            </SmallSelect>
            <Word>des Monats</Word>
          </>
        )}

        {/* Shared time picker for daily / weekly / monthly */}
        {showTime && (
          <>
            <Word>um</Word>
            <SmallSelect
              value={hour}
              onChange={(v) => update({ hour: Number(v) })}
            >
              {HOURS.map((h) => (
                <MenuItem key={h} value={h}>
                  {pad2(h)}
                </MenuItem>
              ))}
            </SmallSelect>
            <Word>:</Word>
            <SmallSelect
              value={minute}
              onChange={(v) => update({ minute: Number(v) })}
            >
              {MINUTES.map((m) => (
                <MenuItem key={m} value={m}>
                  {pad2(m)}
                </MenuItem>
              ))}
            </SmallSelect>
            <Word>Uhr</Word>
          </>
        )}

        {/* Custom: raw cron input */}
        {frequency === "custom" && (
          <TextField
            size="small"
            value={custom_cron}
            onChange={(e) => update({ custom_cron: e.target.value })}
            placeholder="0 6 * * *"
            sx={{ width: 160 }}
            slotProps={{
              inputProps: {
                style: { fontFamily: "monospace" },
              },
            }}
          />
        )}
      </Box>

      {/* Generated cron preview */}
      {cron_preview && frequency !== "custom" && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontFamily: "monospace" }}
        >
          {cron_preview}
        </Typography>
      )}

      {frequency === "manual" && (
        <Typography variant="caption" color="text.secondary">
          Import wird nur manuell ausgeführt.
        </Typography>
      )}
    </Box>
  );
}

CronEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

SmallSelect.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  width: PropTypes.number,
};

Word.propTypes = {
  children: PropTypes.node.isRequired,
};
